import { createTheme } from '@mui/material/styles';

/**
 * The OHIF v2 viewer's look (its :root colours, see styles/themes/viewer.theme.ts),
 * for the report viewer that replaces it on screen. Scoped to the reports UI
 * through a nested ThemeProvider: the rest of the app keeps its own theme.
 */
export const OHIF_COLORS = {
  background: '#000000',
  foreground: '#ffffff',
  // Bars and dropdown menus (--ui-gray-darkest).
  popover: '#151A1F',
  // Active tool / hover colour (--active-color).
  primary: '#20A5D6',
  // Focused menu item (--ui-gray-dark).
  accent: '#16202B',
  // Borders (--ui-border-color).
  input: '#314354',
  // Inactive icons / labels (--default-color).
  mutedForeground: '#9CCEF9',
};

const MUTED_TEXT = OHIF_COLORS.mutedForeground;
const MENU_BORDER = OHIF_COLORS.input;

const ohifViewerTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: OHIF_COLORS.primary, contrastText: OHIF_COLORS.foreground },
    background: { default: OHIF_COLORS.background, paper: OHIF_COLORS.popover },
    text: { primary: OHIF_COLORS.foreground, secondary: MUTED_TEXT },
    divider: MENU_BORDER,
    action: { hover: OHIF_COLORS.accent, selected: OHIF_COLORS.accent },
  },
  typography: {
    fontFamily:
      'Roboto, -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
    fontSize: 13,
  },
  shape: { borderRadius: 4 },
  components: {
    MuiPaper: {
      styleOverrides: { root: { backgroundImage: 'none' } },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          border: `1px solid ${MENU_BORDER}`,
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        },
        list: { padding: 4 },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: 13,
          minHeight: 36,
          padding: '4px 8px',
          borderRadius: 4,
          '&:hover, &.Mui-focusVisible, &.Mui-selected, &.Mui-selected:hover': {
            backgroundColor: OHIF_COLORS.accent,
          },
        },
      },
    },
    MuiListItemText: {
      styleOverrides: { primary: { fontSize: 13 } },
    },
    MuiListItemIcon: {
      styleOverrides: { root: { minWidth: 32, color: 'inherit' } },
    },
    MuiListSubheader: {
      styleOverrides: {
        root: {
          backgroundColor: OHIF_COLORS.popover,
          color: OHIF_COLORS.mutedForeground,
          fontSize: 12,
          lineHeight: '28px',
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        // The toolbar's icon buttons: 40px, rounded, muted until hovered.
        root: ({ ownerState }) => ({
          width: 40,
          height: 40,
          borderRadius: 8,
          ...(ownerState.color !== 'error' && { color: MUTED_TEXT }),
          '& .MuiSvgIcon-root': { fontSize: 22 },
          '&:hover': {
            backgroundColor: OHIF_COLORS.background,
            ...(ownerState.color !== 'error' && { color: OHIF_COLORS.primary }),
          },
          '&.Mui-disabled': { color: MUTED_TEXT, opacity: 0.5 },
        }),
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: OHIF_COLORS.popover,
          color: OHIF_COLORS.foreground,
          border: `1px solid ${MENU_BORDER}`,
          fontSize: 12,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: { textTransform: 'none', borderRadius: 8 },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          color: MUTED_TEXT,
          borderColor: MENU_BORDER,
          textTransform: 'none',
          '&:hover': { backgroundColor: OHIF_COLORS.background, color: OHIF_COLORS.primary },
          '&.Mui-selected, &.Mui-selected:hover': {
            color: OHIF_COLORS.foreground,
            backgroundColor: OHIF_COLORS.primary,
          },
          '&.Mui-disabled': { color: MUTED_TEXT, opacity: 0.5, borderColor: MENU_BORDER },
        },
      },
    },
  },
});

export default ohifViewerTheme;
